import React from "react";
import { ChevronDown, Filter } from "lucide-react";

import type { SeriesData } from "../utils/utils";

type FindingsToolbarProps = {
  activeSeries: string;
  selectedCategoriesCount: number;
  seriesCount: number;
  showFilters: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  visibleSeries: SeriesData[];
  onSeriesChange: (seriesId: string) => void;
  onToggleFilters: () => void;
};

const seriesClasses = (type: string, isActive: boolean) => {
  if (!isActive) {
    return "bg-white dark:bg-gray-800 text-slate-700 dark:text-gray-300 border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700";
  }

  if (type === "OB") {
    return "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-700";
  }
  if (type === "BLIND") {
    return "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-700";
  }
  if (type === "GYN") {
    return "bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400 border-pink-200 dark:border-pink-700";
  }
  return "bg-slate-100 dark:bg-gray-600 text-slate-700 dark:text-gray-300 border-slate-200 dark:border-gray-700";
};

const seriesDotClass = (type: string) => {
  if (type === "OB") return "bg-purple-500";
  if (type === "BLIND") return "bg-blue-500";
  if (type === "GYN") return "bg-pink-500";
  return "bg-slate-500 dark:bg-gray-500";
};

export const FindingsToolbar = ({
  activeSeries,
  selectedCategoriesCount,
  seriesCount,
  showFilters,
  t,
  visibleSeries,
  onSeriesChange,
  onToggleFilters,
}: FindingsToolbarProps) => (
  <div className="flex-shrink-0 mb-4 p-4">
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-2">
        <button
          onClick={() => onSeriesChange("Report")}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeSeries === "Report"
            ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-700"
            : "bg-white dark:bg-gray-800 text-slate-700 dark:text-gray-300 border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700"
            }`}
        >
          <i className="ri-file-list-3-line text-xs"></i>
          {t("series.report")}
        </button>

        {visibleSeries.map((serie) => (
          <button
            key={serie.id}
            onClick={() => onSeriesChange(serie.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${seriesClasses(serie.type, activeSeries === serie.id)}`}
          >
            <div className={`w-2 h-2 rounded-full ${seriesDotClass(serie.type)}`} />
            {serie.type}
          </button>
        ))}

        {visibleSeries.length > 1 && visibleSeries.length === seriesCount && (
          <button
            onClick={() => onSeriesChange("All")}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeSeries === "All"
              ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-700"
              : "bg-white dark:bg-gray-800 text-slate-700 dark:text-gray-300 border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-700"
              }`}
          >
            <i className="ri-layers-line text-xs"></i>
            {t("series.all", { count: visibleSeries.length })}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={onToggleFilters}
        aria-expanded={showFilters}
        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg shadow-sm hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
      >
        <Filter size={16} className="text-slate-600 dark:text-gray-400" />
        <span className="text-sm font-medium text-slate-700 dark:text-gray-300">
          {showFilters ? t("filters.hide") : t("filters.show")}
        </span>
        {selectedCategoriesCount > 0 && (
          <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-600 rounded-full">
            {selectedCategoriesCount}
          </span>
        )}
        <ChevronDown
          size={16}
          className={`text-slate-400 dark:text-gray-500 transition-transform ${showFilters ? "rotate-180" : ""}`}
        />
      </button>
    </div>
  </div>
);
